import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usePayoutRuns, useRunPayout, usePayoutRun, useSetPayoutTier, useRevenueSuggestion } from '../../hooks/usePayout';
import { useAdminCatalog } from '../../hooks/useMovie';
import { getApiErrorMessage } from '../../api/errors';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const inputClass =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50 focus:bg-white/[0.05]';

const TIER_LABELS = {
  TierA_PremiumOriginal: 'Tier A — Premium Original',
  TierB_LicensedHit: 'Tier B — Licensed Hit',
  TierC_IndieNew: 'Tier C — Indie / New',
};

const RUN_STATUS_STYLES = {
  Finalized: 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/25',
  Draft: 'bg-primary/10 text-primary ring-primary/25',
};

function nairaFmt(n) {
  return `₦${Number(n || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;
}

function thisMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function EmptyState({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl py-16 text-center ring-1 ring-foreground/[0.06]">
      <Icon icon={icon} className="size-6 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const style = RUN_STATUS_STYLES[status] || 'bg-white/5 text-muted-foreground ring-white/10';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${style}`}>
      {status}
    </span>
  );
}

function StatCard({ icon, value, label }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-white/[0.03] p-4 ring-1 ring-foreground/[0.06]">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-white/5 text-primary ring-1 ring-white/10">
        <Icon icon={icon} className="size-4" />
      </span>
      <div className="min-w-0">
        <div className="truncate text-lg font-semibold text-foreground">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

function RunForm({ onComputed }) {
  const [month, setMonth] = useState(thisMonthValue());
  const [gross, setGross] = useState('');
  const [deductions, setDeductions] = useState('0');
  const [finalize, setFinalize] = useState(false);
  const [grossTouched, setGrossTouched] = useState(false);
  const runMutation = useRunPayout();

  const [yearStr, monthStr] = month ? month.split('-') : [null, null];
  const suggestionYear = yearStr ? parseInt(yearStr, 10) : null;
  const suggestionMonth = monthStr ? parseInt(monthStr, 10) : null;
  const { data: suggestion, isLoading: suggestionLoading } = useRevenueSuggestion(suggestionYear, suggestionMonth);

  // Auto-fill gross revenue from actual subscription payments the first time a suggestion loads
  // for this period, but never overwrite something the admin has already typed/edited.
  useEffect(() => {
    if (suggestion && !grossTouched && gross === '') {
      setGross(String(suggestion.suggestedGrossRevenueNaira));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion]);

  // Reset the "touched" flag when the period changes so the new period's suggestion can autofill too.
  useEffect(() => {
    setGrossTouched(false);
    setGross('');
  }, [month]);

  const applySuggestion = () => {
    if (!suggestion) return;
    setGross(String(suggestion.suggestedGrossRevenueNaira));
    setGrossTouched(false);
  };

  const handleRun = async () => {
    if (!month || gross === '') {
      toast.error('Enter a month and gross revenue.');
      return;
    }
    const payload = {
      year: parseInt(yearStr, 10),
      month: parseInt(monthStr, 10),
      grossRevenueNaira: parseFloat(gross),
      deductionsNaira: parseFloat(deductions) || 0,
      finalize,
    };
    try {
      await runMutation.mutateAsync(payload);
      toast.success(finalize ? 'Payout run finalized.' : 'Draft computed.');
      onComputed?.(payload.year, payload.month);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to run payout.'));
    }
  };

  return (
    <div className="mb-6 rounded-xl bg-white/[0.03] p-5 ring-1 ring-foreground/[0.06] sm:p-6">
      <h2 className="mb-4 text-sm font-semibold text-foreground">Run a Payout</h2>
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Period">
          <input type="month" className={inputClass} value={month} onChange={(e) => setMonth(e.target.value)} />
        </Field>
        <Field label="Gross revenue (₦)">
          <input
            type="number"
            className={inputClass}
            value={gross}
            onChange={(e) => { setGross(e.target.value); setGrossTouched(true); }}
            placeholder="e.g. 100000000"
          />
        </Field>
        <Field label="Deductions (₦)">
          <input type="number" className={inputClass} value={deductions} onChange={(e) => setDeductions(e.target.value)} placeholder="fees / VAT / refunds" />
        </Field>
      </div>

      <div className="mb-4 flex min-h-5 flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {suggestionLoading ? (
          <span>Checking actual subscription payments…</span>
        ) : suggestion ? (
          <>
            <span>
              Suggested from {suggestion.subscriptionCount} subscription payment{suggestion.subscriptionCount === 1 ? '' : 's'} this period:{' '}
              <strong className="text-foreground/80">{nairaFmt(suggestion.suggestedGrossRevenueNaira)}</strong>
            </span>
            <button
              type="button"
              onClick={applySuggestion}
              className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-0.5 text-primary transition-colors hover:bg-white/5"
            >
              <Icon icon="ph:arrow-clockwise-bold" className="size-3" /> Use this
            </button>
          </>
        ) : null}
      </div>

      <label className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          checked={finalize}
          onChange={(e) => setFinalize(e.target.checked)}
          className="size-4 rounded border-white/20 bg-white/5 accent-primary"
        />
        Finalize (locks this period — cannot be recomputed after)
      </label>

      <Button onClick={handleRun} disabled={runMutation.isPending} className="gap-1.5">
        <Icon icon={finalize ? 'ph:lock-bold' : 'ph:play-bold'} />
        {runMutation.isPending ? 'Running…' : finalize ? 'Finalize Payout' : 'Compute Draft'}
      </Button>
    </div>
  );
}

function RunBreakdown({ year, month }) {
  const { data: run, isLoading, error } = usePayoutRun(year, month);

  if (isLoading) return <EmptyState icon="ph:spinner-gap-bold">Loading run…</EmptyState>;
  if (error || !run) return <EmptyState icon="ph:info-bold">No payout run exists for that period yet.</EmptyState>;

  return (
    <div>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard icon="ph:activity-bold" value={run.Status} label="Status" />
        <StatCard icon="ph:currency-circle-dollar-bold" value={nairaFmt(run.NetRevenueNaira)} label="Net Revenue" />
        <StatCard icon="ph:vault-bold" value={nairaFmt(run.ContentPoolNaira)} label="Content Pool" />
        <StatCard icon="ph:clock-bold" value={run.TotalPlatformQualifiedMinutes.toLocaleString()} label="Qualified Minutes" />
      </div>

      {run.Results.length === 0 ? (
        <EmptyState icon="ph:film-slate-bold">No published titles yet.</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-xl ring-1 ring-foreground/[0.06]">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/5 text-xs uppercase tracking-wider text-muted-foreground/60">
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">Tier</th>
                <th className="px-4 py-3 font-medium">Viewers</th>
                <th className="px-4 py-3 font-medium">Watch Hrs</th>
                <th className="px-4 py-3 font-medium">Completion</th>
                <th className="px-4 py-3 font-medium">New Subs</th>
                <th className="px-4 py-3 font-medium">TPS</th>
                <th className="px-4 py-3 font-medium">Pool Share</th>
                <th className="px-4 py-3 font-medium">Payout</th>
              </tr>
            </thead>
            <tbody>
              {run.Results.map((r) => (
                <tr key={r.MovieId} className="border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-foreground">
                    {r.MovieTitle}
                    {r.WasInDiscoveryWindow && (
                      <span className="ml-1.5 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary ring-1 ring-primary/25">
                        New
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{TIER_LABELS[r.Tier] || r.Tier}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.UniqueViewers}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.WatchHours}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.CompletionRatePercent}%</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.NewSubscribersAcquired}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.TitlePerformanceScore}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.PoolSharePercent}%</td>
                  <td className="px-4 py-3 font-semibold text-primary">{nairaFmt(r.FinalPayoutNaira)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TierAssignment() {
  const [search, setSearch] = useState('');
  const { data } = useAdminCatalog(1, 50, search);
  const setTier = useSetPayoutTier();
  const movies = data?.data ?? [];

  const handleChange = async (movieId, tier) => {
    try {
      await setTier.mutateAsync({ movieId, tier });
      toast.success('Tier updated.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update tier.'));
    }
  };

  return (
    <div className="mt-6 rounded-xl bg-white/[0.03] p-5 ring-1 ring-foreground/[0.06] sm:p-6">
      <h2 className="mb-4 text-sm font-semibold text-foreground">Catalog Tiers</h2>
      <div className="relative mb-4 max-w-sm">
        <Icon icon="ph:magnifying-glass-bold" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/60" />
        <input
          className={`${inputClass} pl-9`}
          placeholder="Search titles…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {movies.length === 0 ? (
        <EmptyState icon="ph:film-slate-bold">No titles match your search.</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-xl ring-1 ring-foreground/[0.06]">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/5 text-xs uppercase tracking-wider text-muted-foreground/60">
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">Tier</th>
              </tr>
            </thead>
            <tbody>
              {movies.map((m) => (
                <tr key={m.Id} className="border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-foreground">{m.Title}</td>
                  <td className="px-4 py-3">
                    <Select
                      value={m.PayoutTier}
                      onValueChange={(value) => handleChange(m.Id, value)}
                      disabled={setTier.isPending}
                    >
                      <SelectTrigger className="w-64">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(TIER_LABELS).map(([val, label]) => (
                          <SelectItem key={val} value={val}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminPayoutRunsPage() {
  usePageTitle('Payout Runs · Kast');
  const { data: runs = [] } = usePayoutRuns();
  const [selected, setSelected] = useState(null); // { year, month }

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-primary ring-1 ring-white/10">
            <Icon icon="ph:currency-dollar-bold" className="size-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Payouts</h1>
            <p className="text-xs text-muted-foreground">Compute or finalize monthly producer payouts, and manage catalog tiers</p>
          </div>
        </div>
        <Button size="sm" variant="secondary" asChild className="gap-1.5">
          <Link to="/admin/payouts/config">
            <Icon icon="ph:gear-six-bold" />
            Configuration
          </Link>
        </Button>
      </div>

      <RunForm onComputed={(year, month) => setSelected({ year, month })} />

      <h2 className="mb-3 text-sm font-semibold text-foreground">Past Runs</h2>
      {runs.length === 0 ? (
        <EmptyState icon="ph:currency-circle-dollar-bold">No payout runs yet — compute one above</EmptyState>
      ) : (
        <div className="mb-6 overflow-x-auto rounded-xl ring-1 ring-foreground/[0.06]">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/5 text-xs uppercase tracking-wider text-muted-foreground/60">
                <th className="px-4 py-3 font-medium">Period</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Net Revenue</th>
                <th className="px-4 py-3 font-medium">Content Pool</th>
                <th className="px-4 py-3 font-medium">Titles</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr
                  key={r.Id}
                  onClick={() => setSelected({ year: r.Year, month: r.Month })}
                  className="cursor-pointer border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.02]"
                >
                  <td className="px-4 py-3 text-foreground">{r.Year}-{String(r.Month).padStart(2, '0')}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.Status} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{nairaFmt(r.NetRevenueNaira)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{nairaFmt(r.ContentPoolNaira)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.TitleCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            Breakdown — {selected.year}-{String(selected.month).padStart(2, '0')}
          </h2>
          <RunBreakdown year={selected.year} month={selected.month} />
        </>
      )}

      <TierAssignment />
    </div>
  );
}
